// AI-powered email intelligence service
// Integrated with GPT-5 Orchestrator, GPT-4-mini Web Search, and Deep Research
// UNIFIED ARCHITECTURE: All email operations route through gpt5NanoAgent for consistency
import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';
import type { Email, Contact, EmailCategory, EmailPriority, EmailSentiment, SuggestedAction, ExtractedEntity, ContactIntelligence } from '../types/email';
import { gpt5Orchestrator, streamOrchestration } from './gpt5Orchestrator';
// NOTE: emailActions import removed - now using unified gpt5NanoAgent for all email operations



// Provider-aware text generation using secure API proxy
function getAiPrefs() {
  try {
    const provider = (typeof window !== 'undefined' && localStorage.getItem('ai_provider')) || 'openai';
    const model = (typeof window !== 'undefined' && localStorage.getItem('ai_model')) || (provider==='openai' ? 'gpt-5' : provider==='anthropic' ? 'claude-4.5' : '');
    return { provider, model } as { provider: 'blink'|'openai'|'anthropic'; model: string };
  } catch { return { provider: 'openai', model: 'gpt-5' } as any; }
}

// GPT-4-mini with web search capability using Blink AI
async function callGPT4MiniWithWebSearch(prompt: string, maxTokens?: number): Promise<string> {
  // Use Blink AI with web search enabled (no external API key needed)
  const { text } = await blink.ai.generateText({ prompt, maxTokens: maxTokens ?? 800, search: true });
  return text;
}

async function callOpenAIChat(prompt: string, model: string, maxTokens?: number, useWebSearch: boolean = false): Promise<string> {
  // Use Blink AI directly with web search when needed
  // This provides GPT-4-level intelligence without requiring external API keys
  const { text } = await blink.ai.generateText({ prompt, maxTokens, search: useWebSearch });
  return text;
}

async function callAnthropicMessages(prompt: string, model: string, maxTokens?: number): Promise<string> {
  const tryWith = async (secretName: string) => {
    const res: any = await blink.data.fetch({
      url: 'https://api.anthropic.com/v1/messages',
      method: 'POST',
      headers: { 'x-api-key': `{{${secretName}}}`, 'anthropic-version': '2023-06-01', 'content-type': 'application/json' },
      body: { model, max_tokens: maxTokens ?? 500, messages: [{ role: 'user', content: prompt }] }
    });
    const content = res.body?.content;
    if (Array.isArray(content) && content[0]?.text) return content[0].text;
    return res.body?.output_text || '';
  };
  try { return await tryWith('ANTHROPIC_API_KEY'); } catch {}
  try { return await tryWith('anthropic_api_key'); } catch {}
  const { text } = await blink.ai.generateText({ prompt, maxTokens });
  return text;
}

async function generateTextSmart(prompt: string, maxTokens?: number, options?: { useWebSearch?: boolean; useOrchestration?: boolean }): Promise<string> {
  const { provider, model } = getAiPrefs();
  
  // Use GPT-5 orchestration for complex requests (now using Blink AI internally)
  if (options?.useOrchestration) {
    const plan = await gpt5Orchestrator.planExecution(prompt);
    return await gpt5Orchestrator.execute(plan);
  }
  
  // All providers now use Blink AI with optional web search
  if (provider === 'openai') return await callOpenAIChat(prompt, model || 'gpt-5', maxTokens, options?.useWebSearch);
  if (provider === 'anthropic') return await callAnthropicMessages(prompt, model || 'claude-4.5', maxTokens);
  const { text } = await blink.ai.generateText({ prompt, maxTokens, search: options?.useWebSearch });
  return text;
}


export class EmailAIService {
  // Analyze email content and extract intelligence
  async analyzeEmail(email: Partial<Email>): Promise<{
    category: EmailCategory;
    priority: EmailPriority;
    sentiment: EmailSentiment;
    actionRequired: boolean;
    suggestedActions: SuggestedAction[];
    smartSummary: string;
    extractedEntities: ExtractedEntity[];
    aiScore: number;
  }> {
    try {
      const { object } = await blink.ai.generateObject({
        prompt: `Analyze this email and extract intelligence:
          From: ${email.from?.email}
          Subject: ${email.subject}
          Body: ${email.body || email.snippet}
          
          Provide comprehensive analysis including category, priority, sentiment, whether action is required,
          suggested actions, a smart summary, and extracted entities (dates, people, tasks, etc).`,
        schema: {
          type: 'object',
          properties: {
            category: { 
              type: 'string', 
              enum: ['urgent', 'important', 'newsletter', 'social', 'promotional', 'transactional', 'personal', 'work', 'spam'] 
            },
            priority: { 
              type: 'string', 
              enum: ['critical', 'high', 'medium', 'low'] 
            },
            sentiment: { 
              type: 'string', 
              enum: ['positive', 'neutral', 'negative', 'urgent'] 
            },
            actionRequired: { type: 'boolean' },
            suggestedActions: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  type: { type: 'string', enum: ['reply', 'forward', 'schedule', 'archive', 'delegate', 'followup'] },
                  title: { type: 'string' },
                  description: { type: 'string' },
                  draftContent: { type: 'string' }
                }
              }
            },
            smartSummary: { type: 'string' },
            extractedEntities: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  type: { type: 'string', enum: ['date', 'person', 'organization', 'location', 'money', 'task', 'link'] },
                  value: { type: 'string' },
                  context: { type: 'string' },
                  confidence: { type: 'number' }
                }
              }
            },
            aiScore: { type: 'number' }
          },
          required: ['category', 'priority', 'sentiment', 'actionRequired', 'suggestedActions', 'smartSummary', 'extractedEntities', 'aiScore']
        }
      });

      return object as any;
    } catch (error) {
      console.error('Error analyzing email:', error);
      // Return default values
      return {
        category: 'personal',
        priority: 'medium',
        sentiment: 'neutral',
        actionRequired: false,
        suggestedActions: [],
        smartSummary: email.snippet || '',
        extractedEntities: [],
        aiScore: 50
      };
    }
  }

  // Generate smart reply suggestions
  async generateReplyDraft(email: Email, replyType: 'positive' | 'negative' | 'neutral' | 'question'): Promise<string> {
    try {
      const text = await generateTextSmart(`Generate a professional email reply:
          Original email from: ${email.from.name} <${email.from.email}>
          Subject: ${email.subject}
          Body: ${email.body}
          
          Generate a ${replyType} reply that is:
          - Professional and appropriate
          - Concise but complete
          - Matches the tone of the original email
          - Includes relevant context`, 500);
      return text;
    } catch (error) {
      console.error('Error generating reply:', error);
      return '';
    }
  }

  // Analyze contact intelligence
  async analyzeContact(emails: Email[]): Promise<ContactIntelligence> {
    if (emails.length === 0) {
      return {
        communicationStyle: 'formal',
        responseTime: 'normal',
        topics: [],
        sentiment: 0,
        lastInteraction: new Date(),
        interactionCount: 0,
        preferredChannel: 'email'
      };
    }

    try {
      const emailSummary = emails.slice(0, 10).map(e => ({
        subject: e.subject,
        snippet: e.snippet,
        date: e.date
      }));

      const { object } = await blink.ai.generateObject({
        prompt: `Analyze these email interactions to understand the contact's communication patterns:
          ${JSON.stringify(emailSummary)}
          
          Determine their communication style, typical response time, main topics, overall sentiment, and preferences.`,
        schema: {
          type: 'object',
          properties: {
            communicationStyle: { type: 'string', enum: ['formal', 'casual', 'technical', 'friendly'] },
            responseTime: { type: 'string', enum: ['immediate', 'quick', 'normal', 'slow'] },
            topics: { type: 'array', items: { type: 'string' } },
            sentiment: { type: 'number' },
            preferredChannel: { type: 'string', enum: ['email', 'chat', 'call'] }
          },
          required: ['communicationStyle', 'responseTime', 'topics', 'sentiment', 'preferredChannel']
        }
      });

      return {
        ...object as any,
        lastInteraction: new Date(emails[0].date),
        interactionCount: emails.length
      };
    } catch (error) {
      console.error('Error analyzing contact:', error);
      return {
        communicationStyle: 'formal',
        responseTime: 'normal',
        topics: [],
        sentiment: 0,
        lastInteraction: new Date(),
        interactionCount: emails.length,
        preferredChannel: 'email'
      };
    }
  }

  // Generate auto-categorization rules
  async generateCategorizationRules(emails: Email[]): Promise<Array<{
    pattern: string;
    category: EmailCategory;
    confidence: number;
  }>> {
    try {
      const { object } = await blink.ai.generateObject({
        prompt: `Based on these email samples, generate smart categorization rules:
          ${emails.slice(0, 5).map(e => `Subject: ${e.subject}, From: ${e.from.email}`).join('\n')}
          
          Create pattern-based rules for automatic email categorization.`,
        schema: {
          type: 'object',
          properties: {
            rules: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  pattern: { type: 'string' },
                  category: { type: 'string', enum: ['urgent', 'important', 'newsletter', 'social', 'promotional', 'transactional', 'personal', 'work', 'spam'] },
                  confidence: { type: 'number' }
                }
              }
            }
          },
          required: ['rules']
        }
      });

      return (object as any).rules;
    } catch (error) {
      console.error('Error generating rules:', error);
      return [];
    }
  }

  // Summarize email thread
  async summarizeThread(emails: Email[]): Promise<string> {
    if (emails.length === 0) return '';

    try {
      const text = await generateTextSmart(`Summarize this email thread concisely:
          ${emails.map(e => `From: ${e.from.name}, Date: ${e.date}, Message: ${e.snippet}`).join('\n')}
          
          Provide a brief summary highlighting key points, decisions, and action items.`, 200);

      return text;
    } catch (error) {
      console.error('Error summarizing thread:', error);
      return emails[0].snippet || '';
    }
  }

  // Extract tasks from email
  async extractTasks(email: Email): Promise<Array<{
    task: string;
    dueDate?: Date;
    assignee?: string;
    priority: 'high' | 'medium' | 'low';
  }>> {
    try {
      const { object } = await blink.ai.generateObject({
        prompt: `Extract actionable tasks from this email:
          Subject: ${email.subject}
          Body: ${email.body}
          
          Identify specific tasks, deadlines, and assignments.`,
        schema: {
          type: 'object',
          properties: {
            tasks: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  task: { type: 'string' },
                  dueDate: { type: 'string' },
                  assignee: { type: 'string' },
                  priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                }
              }
            }
          },
          required: ['tasks']
        }
      });

      return (object as any).tasks.map((t: any) => ({
        ...t,
        dueDate: t.dueDate ? new Date(t.dueDate) : undefined
      }));
    } catch (error) {
      console.error('Error extracting tasks:', error);
      return [];
    }
  }

  // Personal intelligence learning
  async learnPersonalPatterns(emails: Email[]): Promise<{
    writingStyle: string;
    commonPhrases: string[];
    typicalResponseTime: string;
    workingHours: { start: number; end: number };
    priorities: string[];
  }> {
    try {
      const { object } = await blink.ai.generateObject({
        prompt: `Analyze these sent emails to learn personal communication patterns:
          ${emails.slice(0, 10).map(e => `Time: ${e.date}, Subject: ${e.subject}, Snippet: ${e.snippet}`).join('\n')}
          
          Identify writing style, common phrases, response patterns, working hours, and priorities.`,
        schema: {
          type: 'object',
          properties: {
            writingStyle: { type: 'string' },
            commonPhrases: { type: 'array', items: { type: 'string' } },
            typicalResponseTime: { type: 'string' },
            workingHours: {
              type: 'object',
              properties: {
                start: { type: 'number' },
                end: { type: 'number' }
              }
            },
            priorities: { type: 'array', items: { type: 'string' } }
          },
          required: ['writingStyle', 'commonPhrases', 'typicalResponseTime', 'workingHours', 'priorities']
        }
      });

      return object as any;
    } catch (error) {
      console.error('Error learning patterns:', error);
      return {
        writingStyle: 'professional',
        commonPhrases: [],
        typicalResponseTime: 'within 24 hours',
        workingHours: { start: 9, end: 17 },
        priorities: []
      };
    }
  }
}

export const emailAI = new EmailAIService();

/**
 * Generate chat responses with optional web search and orchestration
 * Supports calendar event management commands AND full email inbox operations
 * 
 * UNIFIED ARCHITECTURE: All email operations now route through gpt5NanoAgent
 * for consistent API tracing and agentic logic with AI Task Assistant
 */
export async function chatGenerate(
  prompt: string, 
  maxTokens: number = 500, 
  options?: { 
    useWebSearch?: boolean; 
    useOrchestration?: boolean;
    userId?: string;
  }
): Promise<string> {
  // UNIFIED: Email actions are now handled by gpt5NanoAgent
  // No need to initialize emailActions directly - handled by unified agent system
  
  // Check if this is a calendar-related command
  if (options?.userId && isCalendarCommand(prompt)) {
    return await handleCalendarCommand(prompt, options.userId);
  }
  
  // Check if this is an email action command
  // UNIFIED: Routes through gpt5NanoAgent for consistent API calls
  if (options?.userId && isEmailActionCommand(prompt)) {
    return await handleEmailActionCommand(prompt, options.userId);
  }
  
  return await generateTextSmart(prompt, maxTokens, options);
}

// Check if prompt is an email action command
function isEmailActionCommand(prompt: string): boolean {
  const emailActionKeywords = [
    'send', 'compose', 'draft', 'reply', 'forward',
    'archive', 'delete', 'trash', 'star', 'unstar',
    'mark', 'read', 'unread', 'important',
    'snooze', 'mute', 'pin', 'unpin',
    'label', 'move', 'folder',
    'block', 'unsubscribe', 'spam',
    'search', 'find', 'filter',
    'create rule', 'auto reply', 'schedule',
    'email', 'inbox', 'mail'
  ];
  
  const lowerPrompt = prompt.toLowerCase();
  return emailActionKeywords.some(keyword => lowerPrompt.includes(keyword));
}

/**
 * Handle email action commands
 * 
 * UNIFIED ARCHITECTURE DOCUMENTATION:
 * ====================================
 * 
 * This function has been refactored to use the unified agent system for ALL email operations.
 * Previously, it had separate implementations that called emailActions directly, causing
 * inconsistencies between the AI Assistant and AI Task Assistant.
 * 
 * BEFORE (Inconsistent):
 * ----------------------
 * AIChat.tsx (AI Task Assistant) → gpt5NanoAgent → tracedEmailActions → emailActions → Microsoft Graph API
 * emailAI.ts (AI Assistant)      → emailActions → Microsoft Graph API (DIRECT, bypassing tracing)
 * 
 * AFTER (Unified):
 * ----------------
 * AIChat.tsx (AI Task Assistant) → gpt5NanoAgent → tracedEmailActions → emailActions → Microsoft Graph API
 * emailAI.ts (AI Assistant)      → gpt5NanoAgent → tracedEmailActions → emailActions → Microsoft Graph API
 *                                   ^^^^^^^^^^^^^^
 *                                   NOW USES SAME PATH!
 * 
 * BENEFITS:
 * ---------
 * ✅ Consistent API tracing across all assistants
 * ✅ Unified agentic logic (inbox search first, action validation, etc.)
 * ✅ Same error handling and retry mechanisms
 * ✅ Centralized monitoring and debugging
 * ✅ No duplicate code maintenance
 * 
 * ALL email operations (send, search, reply, forward, archive, delete, etc.) 
 * now flow through the same gpt5NanoAgent system used by the AI Task Assistant.
 */
async function handleEmailActionCommand(prompt: string, userId: string): Promise<string> {
  try {
    // CRITICAL: Import and use gpt5NanoAgent for UNIFIED agentic execution
    // This ensures ALL AI assistants use the same API calls and agentic logic
    const { executeUserIntent } = await import('./gpt5NanoAgent');
    
    // ALL commands now go through the unified agent system
    // This ensures consistent API calls, tracing, and agentic logic
    const result = await executeUserIntent(prompt);
    
    if (result.success && result.execution_result) {
      const executionResult = result.execution_result;
      
      // Format response based on action type
      if (executionResult.action === 'email_send') {
        return `✅ Email sent successfully to ${executionResult.recipients.join(', ')}!\n\n**Message ID:** ${executionResult.messageId}\n**Timestamp:** ${new Date(executionResult.timestamp).toLocaleString()}\n\nThe email was sent via Microsoft Graph API using your connected account.`;
      } else if (executionResult.action === 'email_search') {
        let response = `🔍 **Email Search Results**\n\n**Query:** "${executionResult.query}"\n**Found:** ${executionResult.resultsCount} email(s)\n\n`;
        
        if (executionResult.results && executionResult.results.length > 0) {
          executionResult.results.slice(0, 10).forEach((email: any, idx: number) => {
            response += `${idx + 1}. **${email.subject || '(No Subject)'}**\n`;
            response += `   From: ${email.senderName || email.senderEmail}\n`;
            response += `   Date: ${new Date(email.receivedDate).toLocaleString()}\n`;
            if (email.snippet) {
              response += `   ${email.snippet.substring(0, 100)}...\n`;
            }
            response += `\n`;
          });
        } else {
          response += `No emails found matching your criteria.\n`;
        }
        
        return response;
      } else if (executionResult.action === 'get_recent_emails') {
        let response = `📧 **Recent Emails**\n\n**Timeframe:** ${executionResult.timeframe}\n**Found:** ${executionResult.resultsCount} email(s)\n\n`;
        
        if (executionResult.results && executionResult.results.length > 0) {
          executionResult.results.slice(0, 10).forEach((email: any, idx: number) => {
            response += `${idx + 1}. **${email.subject || '(No Subject)'}**\n`;
            response += `   From: ${email.senderName || email.senderEmail}\n`;
            response += `   Date: ${new Date(email.receivedDate).toLocaleString()}\n`;
            if (email.snippet) {
              response += `   ${email.snippet.substring(0, 100)}...\n`;
            }
            response += `\n`;
          });
        }
        
        return response;
      } else if (executionResult.action === 'answer_question') {
        // General question answered with inbox context
        return executionResult.answer || executionResult.information || result.agent_response?.reasoning || 'Unable to answer based on email context.';
      } else {
        // Generic success response
        return `✅ **Action Completed**\n\n${JSON.stringify(executionResult, null, 2)}`;
      }
    } else {
      return `❌ **Error processing email action**\n\n${result.error || 'Unknown error occurred'}\n\n${result.agent_response ? `**Agent Response:**\n${result.agent_response.thinking}` : ''}`;
    }
    
    /* OLD CODE - REPLACED BY UNIFIED AGENT SYSTEM
    // Send/Compose email
    if (lowerPrompt.includes('send') && !lowerPrompt.includes('schedule')) {
      // Parse email details from prompt using AI
      const { object } = await blink.ai.generateObject({
        prompt: `Extract email details from this request: "${prompt}"\n\nProvide recipient email(s), subject, and body content.`,
        schema: {
          type: 'object',
          properties: {
            to: { type: 'array', items: { type: 'string' } },
            cc: { type: 'array', items: { type: 'string' } },
            subject: { type: 'string' },
            body: { type: 'string' },
            isImportant: { type: 'boolean' }
          },
          required: ['to', 'subject', 'body']
        }
      }) as any;
      
      const result = await emailActions.sendEmail({
        to: object.to,
        cc: object.cc,
        subject: object.subject,
        body: object.body,
        htmlBody: `<div>${object.body}</div>`
      });
      
      return `✅ Email sent successfully to ${object.to.join(', ')}!\n\n**Subject:** ${object.subject}\n\nMessage ID: ${result.messageId}`;
    }
    */
    
    /* OLD REPLY/ARCHIVE/DELETE CODE - NOW HANDLED BY UNIFIED AGENT
    // All these operations are now routed through gpt5NanoAgent
    // This ensures consistent API tracing and agentic logic
    // The unified agent system automatically handles:
    // - Reply/Forward
    // - Archive/Delete/Trash
    // - Star/Unstar
    // - Mark Read/Unread
    // - Search
    // - Snooze
    // - Block Sender
    // - Auto-Reply
    */
    
  } catch (error) {
    console.error('Error handling email action command:', error);
    return `I encountered an error processing your email request. ${error instanceof Error ? error.message : 'Please try again.'}`;
  }
}

// Check if prompt is calendar-related
function isCalendarCommand(prompt: string): boolean {
  const calendarKeywords = [
    'calendar', 'event', 'meeting', 'schedule', 'appointment',
    'remind', 'book', 'plan', 'add to calendar', 'create event',
    'update event', 'delete event', 'find event', 'show events',
    'what\'s on my calendar', 'when is', 'reschedule'
  ];
  
  const lowerPrompt = prompt.toLowerCase();
  return calendarKeywords.some(keyword => lowerPrompt.includes(keyword));
}

// Handle calendar-related commands
async function handleCalendarCommand(prompt: string, userId: string): Promise<string> {
  try {
    const { calendarAI } = await import('./calendarAI');
    const { calendarService } = await import('./calendarService');
    
    const lowerPrompt = prompt.toLowerCase();
    
    // Create event
    if (lowerPrompt.includes('create') || lowerPrompt.includes('add') || lowerPrompt.includes('schedule') || lowerPrompt.includes('book')) {
      const event = await calendarAI.createEventFromNaturalLanguage(userId, prompt);
      return `✅ Event created successfully!\n\n**${event.title}**\n📅 ${event.startTime.toLocaleString()} - ${event.endTime.toLocaleTimeString()}\n${event.location ? `📍 ${event.location}\n` : ''}${event.description ? `\n${event.description}` : ''}`;
    }
    
    // Find/show events
    if (lowerPrompt.includes('show') || lowerPrompt.includes('find') || lowerPrompt.includes('what') || lowerPrompt.includes('when')) {
      const events = await calendarAI.findEvents(userId, prompt);
      if (events.length === 0) {
        return 'No events found matching your query.';
      }
      
      let response = `Found ${events.length} event${events.length > 1 ? 's' : ''}:\n\n`;
      events.forEach((event, i) => {
        response += `${i + 1}. **${event.title}**\n`;
        response += `   📅 ${event.startTime.toLocaleString()} - ${event.endTime.toLocaleTimeString()}\n`;
        if (event.location) response += `   📍 ${event.location}\n`;
        response += '\n';
      });
      return response;
    }
    
    // Update event
    if (lowerPrompt.includes('update') || lowerPrompt.includes('change') || lowerPrompt.includes('modify') || lowerPrompt.includes('reschedule')) {
      // First find the event
      const events = await calendarService.getUserEvents(userId);
      if (events.length === 0) {
        return 'No events found to update.';
      }
      
      // Use AI to determine which event to update
      const { object } = await blink.ai.generateObject({
        prompt: `Based on this update request: "${prompt}"\n\nWhich event should be updated?\n\n${events.map((e, i) => `${i}. ${e.title} - ${e.startTime.toISOString()}`).join('\n')}\n\nReturn the index of the event to update.`,
        schema: {
          type: 'object',
          properties: {
            eventIndex: { type: 'number' }
          },
          required: ['eventIndex']
        }
      });
      
      const eventIndex = (object as any).eventIndex;
      if (eventIndex >= 0 && eventIndex < events.length) {
        const event = events[eventIndex];
        const updatedEvent = await calendarAI.updateEventFromNaturalLanguage(event.id, prompt);
        return `✅ Event updated successfully!\n\n**${updatedEvent.title}**\n📅 ${updatedEvent.startTime.toLocaleString()} - ${updatedEvent.endTime.toLocaleTimeString()}\n${updatedEvent.location ? `📍 ${updatedEvent.location}\n` : ''}`;
      }
      
      return 'Could not determine which event to update.';
    }
    
    // Delete event
    if (lowerPrompt.includes('delete') || lowerPrompt.includes('remove') || lowerPrompt.includes('cancel')) {
      const events = await calendarAI.findEvents(userId, prompt);
      if (events.length === 0) {
        return 'No events found to delete.';
      }
      
      // Delete the first matching event
      await calendarService.deleteEvent(events[0].id);
      return `✅ Event "${events[0].title}" has been deleted.`;
    }
    
    // Suggest time
    if (lowerPrompt.includes('suggest') || lowerPrompt.includes('when should') || lowerPrompt.includes('find time')) {
      const suggestion = await calendarAI.suggestEventTime(userId, prompt);
      return `💡 I suggest scheduling this:\n\n📅 ${suggestion.suggestedStart.toLocaleString()} - ${suggestion.suggestedEnd.toLocaleTimeString()}\n\n${suggestion.reason}`;
    }
    
    // Fallback: use general AI response about calendar
    return await generateTextSmart(prompt, maxTokens);
    
  } catch (error) {
    console.error('Error handling calendar command:', error);
    return `I encountered an error processing your calendar request. ${error instanceof Error ? error.message : 'Please try again.'}`;
  }
}

/**
 * Deep research using OpenAI's Deep Research API patterns
 * Comprehensive multi-step research with source synthesis
 */
export async function performDeepResearch(query: string, useFullModel: boolean = true): Promise<string> {
  try {
    const { deepResearch: deepResearchService } = await import('./deepResearch');
    const result = await deepResearchService.research({
      query,
      useFullModel,
      outputFormat: 'markdown',
      requireCitations: true
    });
    
    // Format result with citations
    let output = result.report;
    
    if (result.citations.length > 0) {
      output += '\n\n---\n\n## Sources\n\n';
      result.citations.forEach((citation, i) => {
        output += `${i + 1}. [${citation.title}](${citation.url})\n`;
      });
    }
    
    output += `\n\n---\n\n*Research completed in ${(result.metadata.completion_time / 1000).toFixed(1)}s with ${result.citations.length} sources*`;
    
    return output;
  } catch (error) {
    console.error('Deep research failed, falling back to orchestration:', error);
    // Fallback to GPT-5 orchestration
    const plan = await gpt5Orchestrator.planExecution(query);
    return await gpt5Orchestrator.execute(plan);
  }
}

// High-quality streaming API using Blink SDK
// Streaming with provider routing. If provider is OpenAI or Anthropic, we stream via edge function
const AI_STREAM_URL = 'https://ben3ou87--ai-stream.functions.blink.new'

export function streamChat({
  prompt,
  maxTokens = 800,
  onChunk,
  onDone,
  useWebSearch = false,
  useOrchestration = false,
}: {
  prompt: string;
  maxTokens?: number;
  onChunk: (chunk: string) => void;
  onDone?: (finalText: string) => void;
  useWebSearch?: boolean;
  useOrchestration?: boolean;
}) {
  let cancelled = false
  let full = ''

  const { provider, model } = getAiPrefs()

  // Use orchestration for complex multi-step requests (works with all providers)
  if (useOrchestration) {
    streamOrchestration({
      prompt,
      onProgress: (stage, data) => {
        if (stage === 'planning') onChunk('🧠 Planning execution strategy...\n\n');
        else if (stage === 'executing') onChunk('\n📊 Executing tasks...\n\n');
        else if (stage === 'synthesizing') onChunk('\n✨ Synthesizing results...\n\n');
      },
      onChunk: (chunk) => {
        if (!cancelled) {
          full += chunk;
          onChunk(chunk);
        }
      },
      onComplete: (result) => {
        if (!cancelled) onDone?.(result);
      }
    }).catch(err => {
      console.error('Orchestration streaming failed, falling back:', err);
      // Fallback to Blink SDK on orchestration failure
      if (!cancelled) {
        useFallbackBlinkStream();
      }
    });
    return { cancel: () => { cancelled = true } };
  }

  // Helper function for Blink SDK fallback streaming
  const useFallbackBlinkStream = async () => {
    try {
      full = ''; // Reset
      await blink.ai.streamText({ prompt, maxTokens, search: useWebSearch }, (chunk: string) => {
        if (cancelled) return
        full += chunk
        onChunk(chunk)
      })
      if (!cancelled) onDone?.(full)
    } catch (err) {
      console.error('Blink SDK fallback streaming error:', err)
      if (!cancelled) onDone?.(full)
    }
  }

  const sseStream = async () => {
    try {
      // Note: useWebSearch is not sent to edge function as OpenAI/Anthropic don't support it natively
      // Web search functionality should use Blink SDK instead
      
      // Validate provider and use fallback to Blink SDK if needed
      const validProviders = ['openai', 'anthropic'];
      const effectiveProvider = validProviders.includes(provider) ? provider : 'openai';
      
      // Ensure model is set properly with valid defaults
      let effectiveModel = model;
      if (!effectiveModel || effectiveModel === 'auto' || effectiveModel === 'gpt-5') {
        effectiveModel = effectiveProvider === 'openai' ? 'gpt-4o-mini' : 'claude-3-5-sonnet-20241022';
      }
      
      console.log(`Streaming with: provider=${effectiveProvider}, model=${effectiveModel}`);
      
      const res = await fetch(AI_STREAM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: effectiveProvider,
          model: effectiveModel,
          messages: [{ role: 'user', content: prompt }],
          maxTokens
        })
      })
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Edge function error ${res.status}:`, errorText);
        throw new Error(`Upstream error ${res.status}`);
      }
      
      if (!res.body) {
        console.error('Edge function returned no body');
        throw new Error('No response body');
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (cancelled) { reader.cancel(); break }
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n\n')
        buffer = parts.pop() || ''
        for (const part of parts) {
          const lines = part.split('\n')
          let eventType = 'message'
          let data = ''
          for (const line of lines) {
            if (line.startsWith('event:')) eventType = line.slice(6).trim()
            if (line.startsWith('data:')) data += line.slice(5).trim()
          }
          if (!data || data === '[DONE]') continue
          try {
            const json = safeJsonParse(data, 'emailAI.streamChat - SSE message parsing')
            // OpenAI
            let chunkText = json?.choices?.[0]?.delta?.content || json?.choices?.[0]?.text || ''
            // Anthropic
            if (!chunkText && (eventType === 'content_block_delta' || json?.delta?.text)) {
              chunkText = json?.delta?.text || ''
            }
            if (chunkText) {
              full += chunkText
              onChunk(chunkText)
            }
          } catch {
            // ignore keepalives and parsing errors
          }
        }
      }
      onDone?.(full)
    } catch (err) {
      console.error('sseStream error', err)
      onDone?.(full)
    }
  }

  // Route: Always use Blink SDK for simplicity and reliability
  // Blink SDK handles all providers and web search natively
  ;(async () => {
    try {
      console.log(`Starting Blink SDK stream: search=${useWebSearch}, maxTokens=${maxTokens}`);
      await blink.ai.streamText({ prompt, maxTokens, search: useWebSearch }, (chunk: string) => {
        if (cancelled) return
        full += chunk
        onChunk(chunk)
      })
      if (!cancelled) onDone?.(full)
    } catch (err) {
      console.error('Blink SDK streaming error:', err)
      if (!cancelled) onDone?.(full || 'I apologize, but I encountered an error. Please try again.')
    }
  })()

  return { cancel: () => { cancelled = true } }
}
